import React, { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as firebaseFunctions } from "../../firebase";

const modeIncludesExport = (mode) => mode === "export" || mode === "both";
const modeIncludesImport = (mode) => mode === "import" || mode === "both";
const deriveCalendarMode = ({ calendarEnabled, externalEnabled }) => {
  if (calendarEnabled && externalEnabled) return "both";
  if (calendarEnabled) return "export";
  if (externalEnabled) return "import";
  return "off";
};

export default function IntegrationsCard({ familyId, familySettings, canManage }) {
  const [calendarStatus, setCalendarStatus] = useState("");
  const [calendarToken, setCalendarToken] = useState(familySettings?.calendarSyncToken || "");
  const [calendarEnabled, setCalendarEnabled] = useState(Boolean(familySettings?.calendarSyncEnabled));
  const [externalCalendarUrl, setExternalCalendarUrl] = useState(familySettings?.externalCalendarUrl || "");
  const [calendarImportProvider, setCalendarImportProvider] = useState(
    String(familySettings?.calendarImportProvider || "").toLowerCase()
  );
  const [calendarMode, setCalendarMode] = useState(
    deriveCalendarMode({
      calendarEnabled: Boolean(familySettings?.calendarSyncEnabled),
      externalEnabled: Boolean(familySettings?.externalCalendarEnabled)
    })
  );
  const [externalCalendarStatus, setExternalCalendarStatus] = useState("");
  const [externalSyncing, setExternalSyncing] = useState(false);
  const [calendarOAuthLoading, setCalendarOAuthLoading] = useState(false);
  const syncExternalCalendarNow = httpsCallable(firebaseFunctions, "syncExternalCalendarNow");
  const createCalendarOAuthSession = httpsCallable(firebaseFunctions, "createCalendarOAuthSession");
  const disconnectCalendarImport = httpsCallable(firebaseFunctions, "disconnectCalendarImport");

  const projectId = db.app.options.projectId;
  const calendarPath = calendarToken
    ? `https://us-central1-${projectId}.cloudfunctions.net/familyCalendar/calendar/${familyId}/${calendarToken}`
    : "";
  const calendarWebcal = calendarPath ? calendarPath.replace(/^https:/, "webcal:") : "";
  const googleSubscribeUrl = calendarPath
    ? `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(calendarPath)}`
    : "";
  const outlookSubscribeUrl = calendarPath
    ? `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(calendarPath)}&name=${encodeURIComponent(
        `${familySettings?.familyName || "FamilySync"} Calendar`
      )}`
    : "";

  useEffect(() => {
    const nextCalendarEnabled = Boolean(familySettings?.calendarSyncEnabled);
    const nextExternalEnabled = Boolean(familySettings?.externalCalendarEnabled);
    const nextImportProvider = String(familySettings?.calendarImportProvider || "").toLowerCase();
    setCalendarToken(familySettings?.calendarSyncToken || "");
    setCalendarEnabled(nextCalendarEnabled);
    setExternalCalendarUrl(familySettings?.externalCalendarUrl || "");
    setCalendarImportProvider(nextImportProvider);
    setCalendarMode(
      deriveCalendarMode({
        calendarEnabled: nextCalendarEnabled,
        externalEnabled: nextExternalEnabled
      })
    );
  }, [
    familySettings?.calendarSyncToken,
    familySettings?.calendarSyncEnabled,
    familySettings?.externalCalendarUrl,
    familySettings?.externalCalendarEnabled,
    familySettings?.calendarImportProvider
  ]);

  const newToken = () => {
    if (!window.crypto?.getRandomValues) {
      return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 18)}`;
    }
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  };

  const applyCalendarMode = async (mode) => {
    if (!canManage) return;
    const wantsExport = modeIncludesExport(mode);
    const wantsImport = modeIncludesImport(mode);
    const nextToken = wantsExport ? (calendarToken || newToken()) : calendarToken;
    await updateDoc(doc(db, "families", familyId), {
      calendarSyncEnabled: wantsExport,
      calendarSyncToken: nextToken,
      externalCalendarEnabled: wantsImport,
      calendarSyncUpdatedAt: new Date(),
      externalCalendarUpdatedAt: new Date()
    });
    setCalendarMode(mode);
    setCalendarEnabled(wantsExport);
    if (wantsExport) setCalendarToken(nextToken);
    setCalendarStatus(
      mode === "both"
        ? "Calendar mode set to both directions."
        : mode === "import"
        ? "Calendar mode set to import only."
        : mode === "export"
        ? "Calendar mode set to export only."
        : "Calendar sync turned off."
    );
    const hasImportSource = Boolean(calendarImportProvider || externalCalendarUrl.trim());
    if (wantsImport && !hasImportSource) {
      setExternalCalendarStatus("Connect Google or Microsoft to finish import setup.");
      setTimeout(() => setExternalCalendarStatus(""), 4000);
    }
    setTimeout(() => setCalendarStatus(""), 3000);
  };

  const rotateCalendarToken = async () => {
    if (!canManage) return;
    const nextToken = newToken();
    await updateDoc(doc(db, "families", familyId), {
      calendarSyncEnabled: true,
      calendarSyncToken: nextToken,
      calendarSyncUpdatedAt: new Date(),
    });
    setCalendarToken(nextToken);
    setCalendarEnabled(true);
    setCalendarStatus("Sync link rotated.");
    setTimeout(() => setCalendarStatus(""), 3000);
  };

  const runExternalSyncNow = async () => {
    if (!canManage) return;
    if (!calendarImportProvider && !externalCalendarUrl.trim()) {
      setExternalCalendarStatus("Connect Google or Microsoft first.");
      setTimeout(() => setExternalCalendarStatus(""), 4000);
      return;
    }
    try {
      setExternalSyncing(true);
      await syncExternalCalendarNow({ familyId });
      setExternalCalendarStatus("External calendar synced.");
    } catch (error) {
      setExternalCalendarStatus(error?.message || "External sync failed.");
    } finally {
      setExternalSyncing(false);
      setTimeout(() => setExternalCalendarStatus(""), 4000);
    }
  };

  const connectCalendarImport = async (provider) => {
    if (!canManage) return;
    try {
      setCalendarOAuthLoading(true);
      setExternalCalendarStatus("");
      const result = await createCalendarOAuthSession({
        familyId,
        provider
      });
      const url = result?.data?.url;
      if (!url) throw new Error("OAuth URL was not returned.");
      window.location.assign(url);
    } catch (error) {
      setExternalCalendarStatus(error?.message || "Unable to start calendar connection.");
      setTimeout(() => setExternalCalendarStatus(""), 4000);
      setCalendarOAuthLoading(false);
    }
  };

  const disconnectImport = async () => {
    if (!canManage) return;
    try {
      setCalendarOAuthLoading(true);
      await disconnectCalendarImport({ familyId });
      setExternalCalendarStatus("Calendar import disconnected.");
    } catch (error) {
      setExternalCalendarStatus(error?.message || "Unable to disconnect calendar import.");
    } finally {
      setCalendarOAuthLoading(false);
      setTimeout(() => setExternalCalendarStatus(""), 4000);
    }
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Calendar Sync</h2>
      </div>
      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {canManage && (
          <div className="setting-section">
            <h3>Calendar Sync</h3>
            <small style={{ opacity: 0.7 }}>
              Choose one mode: export only, import only, or both.
            </small>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
              <button
                className={`btn-nav ${calendarMode === "export" ? "active-settings-tab" : ""}`}
                onClick={() => applyCalendarMode("export")}
                type="button"
              >
                Export only
              </button>
              <button
                className={`btn-nav ${calendarMode === "import" ? "active-settings-tab" : ""}`}
                onClick={() => applyCalendarMode("import")}
                type="button"
              >
                Import only
              </button>
              <button
                className={`btn-nav ${calendarMode === "both" ? "active-settings-tab" : ""}`}
                onClick={() => applyCalendarMode("both")}
                type="button"
              >
                Both
              </button>
              <button
                className={`btn-nav ${calendarMode === "off" ? "active-settings-tab" : ""}`}
                onClick={() => applyCalendarMode("off")}
                type="button"
              >
                Off
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {modeIncludesExport(calendarMode) && (
                <button className="btn-nav" onClick={rotateCalendarToken} type="button">
                  Rotate Export Link
                </button>
              )}
            </div>
            {modeIncludesExport(calendarMode) && calendarEnabled && calendarPath && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                <a className="btn-nav" href={googleSubscribeUrl} target="_blank" rel="noreferrer">
                  Google
                </a>
                <a className="btn-nav" href={outlookSubscribeUrl} target="_blank" rel="noreferrer">
                  Microsoft
                </a>
                <a className="btn-nav" href={calendarWebcal || calendarPath} target="_blank" rel="noreferrer">
                  Apple
                </a>
              </div>
            )}
            {calendarStatus && <small style={{ opacity: 0.7 }}>{calendarStatus}</small>}
            {modeIncludesExport(calendarMode) && (
              <small style={{ opacity: 0.7 }}>
                Export is one tap for Apple, Google, and Microsoft.
              </small>
            )}
            {modeIncludesImport(calendarMode) && (
              <>
                <small style={{ opacity: 0.7, display: "block", marginTop: "10px" }}>
                  Import with provider OAuth. Families only tap Connect and approve access.
                </small>
                <div style={{ marginTop: "8px", opacity: 0.8 }}>
                  Connected source:{" "}
                  <strong>
                    {calendarImportProvider
                      ? calendarImportProvider === "google"
                        ? "Google Calendar"
                        : "Microsoft Calendar"
                      : "Not connected"}
                  </strong>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                  <button
                    className="btn-nav"
                    onClick={() => connectCalendarImport("google")}
                    type="button"
                    disabled={calendarOAuthLoading}
                  >
                    Connect Google
                  </button>
                  <button
                    className="btn-nav"
                    onClick={() => connectCalendarImport("microsoft")}
                    type="button"
                    disabled={calendarOAuthLoading}
                  >
                    Connect Microsoft
                  </button>
                  <button
                    className="btn-nav"
                    onClick={runExternalSyncNow}
                    type="button"
                    disabled={externalSyncing}
                  >
                    {externalSyncing ? "Syncing..." : "Sync now"}
                  </button>
                  {(calendarImportProvider || externalCalendarUrl) && (
                    <button
                      className="btn-nav"
                      onClick={disconnectImport}
                      type="button"
                      disabled={calendarOAuthLoading}
                    >
                      Disconnect import
                    </button>
                  )}
                </div>
                {externalCalendarStatus && <small style={{ opacity: 0.7 }}>{externalCalendarStatus}</small>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
